import {
  Component,
  inject,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  OnInit,
  HostListener,
  effect,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AppStateService } from '../../services/app-state.service';
import { Web3Service } from '../../services/web3.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { RoutingService } from '../../services/routing.service';
import { NETWORKS } from '../../config/network.config';
import {
  POSITION_MANAGER_ABI,
  EXTERNAL_ROUTER_ABI,
  ERC20_ABI
} from '../../config/contracts.config';
import { ethers } from 'ethers';
import * as echarts from 'echarts';
import {
  TickMath,
  Position,
  nearestUsableTick,
  encodeSqrtRatioX96,
  FeeAmount,
  Pool,
} from '@uniswap/v3-sdk';
import { Token as UniToken, CurrencyAmount } from '@uniswap/sdk-core';
import { Subscription } from 'rxjs';
import { ActivityService } from '../../services/activity.service';

@Component({
  selector: 'app-new-position',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './new-position.component.html',
})
export class NewPositionComponent implements OnInit, AfterViewInit, OnDestroy {
  public state = inject(AppStateService);
  private web3Service = inject(Web3Service);
  private routingService = inject(RoutingService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private activityService = inject(ActivityService);

  constructor() {
    effect(() => {
      const activeNetworkId = this.state.selectedNetworkId();
      untracked(() => {
        const qParams = this.route.snapshot?.queryParams || {};
        if (qParams['step'] !== '2' && !qParams['token1']) {
          this.resetForm();
        }
        this.fetchPoolTokens();
      });
    });
  }

  public poolTokens: any[] = [];
  public showLocalTokenModal = false;
  public localTokenModalTarget: 'from' | 'to' = 'from';
  public localTokenSearchQuery = '';

  public activeStep = 1;
  public selectedFeeTier = '';
  public rangeMode: 'full' | 'custom' = 'custom';

  public token1 = '';
  public token2 = '';

  public minPrice = 1779.3946;
  public maxPrice = 2248.4958;
  public currentPrice = 2345.67;

  public depositToken1 = '';
  public depositToken2 = '';

  public activeTimeframe = '1M';
  public timeframes = ['1D', '1W', '1M', '1Y', 'All time'];
  public isChartLoading = false;
  public isAutoNavigating = false;
  public historicalPriceData: any[] = [];
  public baseToken: 'from' | 'to' = 'from';

  @ViewChild('chartContainer') chartContainer!: ElementRef;
  private chartInstance: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private draggingLine: 'min' | 'max' | 'range' | null = null;
  private dragStartPrice: number = 0;
  private dragStartMin: number = 0;
  private dragStartMax: number = 0;

  minHandleLeft: number | null = null;
  maxHandleLeft: number | null = null;

  @HostListener('window:mousemove', ['$event'])
  onGlobalMouseMove(event: MouseEvent) {
    if (!this.draggingLine || !this.chartInstance || !this.chartContainer) return;
    const rect = this.chartContainer.nativeElement.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    this.handleDragMove(offsetX, offsetY);
  }

  @HostListener('window:touchmove', ['$event'])
  onGlobalTouchMove(event: TouchEvent) {
    if (!this.draggingLine || !this.chartInstance || !this.chartContainer) return;
    const rect = this.chartContainer.nativeElement.getBoundingClientRect();
    const touch = event.touches[0];
    const offsetX = touch.clientX - rect.left;
    const offsetY = touch.clientY - rect.top;
    this.handleDragMove(offsetX, offsetY);
  }

  startHtmlDrag(event: MouseEvent | TouchEvent, type: 'min' | 'max') {
    event.preventDefault();
    event.stopPropagation();
    this.draggingLine = type;
    document.body.style.cursor = 'ew-resize';
  }

  private updateHandlePositions() {
    if (!this.chartInstance) {
      this.minHandleLeft = null;
      this.maxHandleLeft = null;
      return;
    }
    try {
      const chartWidth = this.chartInstance.getWidth();
      const gridLeft = 20;
      const gridRight = chartWidth - 60;

      let minPixel = this.chartInstance.convertToPixel({ xAxisIndex: 0 }, this.minPrice) as number;
      let maxPixel = this.chartInstance.convertToPixel({ xAxisIndex: 0 }, this.maxPrice) as number;
      
      if (minPixel !== undefined && !isNaN(minPixel)) {
        if (minPixel < gridLeft) minPixel = gridLeft;
        if (minPixel > gridRight) minPixel = gridRight;
        this.minHandleLeft = minPixel;
      }
      if (maxPixel !== undefined && !isNaN(maxPixel)) {
        if (maxPixel < gridLeft) maxPixel = gridLeft;
        if (maxPixel > gridRight) maxPixel = gridRight;
        this.maxHandleLeft = maxPixel;
      }
    } catch(e) {
      this.minHandleLeft = null;
      this.maxHandleLeft = null;
    }
  }

  private handleDragMove(offsetX: number, offsetY: number) {
    if (!this.chartInstance) return;

    // Clamp offsetX to ECharts grid boundaries (left: 20, right: 60)
    const chartWidth = this.chartInstance.getWidth();
    const gridLeft = 20;
    const gridRight = chartWidth - 60;
    
    if (offsetX < gridLeft) offsetX = gridLeft;
    if (offsetX > gridRight) offsetX = gridRight;

    const pointInPixel = [offsetX, offsetY];

    let xValue: number;
    try {
      xValue = this.chartInstance!.convertFromPixel({ xAxisIndex: 0 }, offsetX) as number;
    } catch (e) {
      const pointInGrid = this.chartInstance!.convertFromPixel(
        { seriesIndex: 0 },
        pointInPixel,
      ) as number[];
      if (!pointInGrid) return;
      xValue = pointInGrid[0];
    }

    if (xValue !== undefined && xValue !== null && !isNaN(xValue)) {
      const xAxisMin = this.chartInstance!.convertFromPixel({ xAxisIndex: 0 }, 0) as number;
      const xAxisMax = this.chartInstance!.convertFromPixel({ xAxisIndex: 0 }, this.chartInstance!.getWidth()) as number;

      // Prevent negative prices
      if (xValue < 0) xValue = 0;

      if (this.draggingLine === 'min') {
        if (xValue > this.maxPrice) xValue = this.maxPrice - 0.000001;
        this.minPrice = xValue;
      } else if (this.draggingLine === 'max') {
        if (xValue < this.minPrice) xValue = this.minPrice + 0.000001;
        this.maxPrice = xValue;
      } else if (this.draggingLine === 'range') {
        const delta = xValue - this.dragStartPrice;
        let newMin = this.dragStartMin + delta;
        let newMax = this.dragStartMax + delta;
        if (newMin < 0) {
          newMax -= newMin;
          newMin = 0;
        }
        this.minPrice = newMin;
        this.maxPrice = newMax;
      }

      this.rangeMode = 'custom';
      this.updateChartMarkLines();
      this.onDepositToken1Change();
    }
  }

  @HostListener('window:mouseup')
  onGlobalMouseUp() {
    this.endDrag();
  }

  @HostListener('window:touchend')
  onGlobalTouchEnd() {
    this.endDrag();
  }

  private endDrag() {
    if (this.draggingLine) {
      document.body.style.cursor = 'default';
    }
    this.draggingLine = null;
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      if (params['token1']) {
        this.token1 = params['token1'];
        let fromToken = this.state
          .tokens()
          .find((t) => t.symbol.toUpperCase() === params['token1'].toUpperCase());
        if (!fromToken) {
          fromToken = {
            symbol: this.token1,
            name: this.token1 + ' Token',
            pairName: this.token1 + ' / USDT',
            rate: 100,
            change: 0,
            color: '#888888',
          };
        }
        this.state.fromToken.set(fromToken);
      }
      if (params['token2']) {
        this.token2 = params['token2'];
        let toToken = this.state
          .tokens()
          .find((t) => t.symbol.toUpperCase() === params['token2'].toUpperCase());
        if (!toToken) {
          toToken = {
            symbol: this.token2,
            name: this.token2 + ' Token',
            pairName: this.token2 + ' / USDT',
            rate: 100,
            change: 0,
            color: '#888888',
          };
        }
        this.state.toToken.set(toToken);
      }

      if (params['fee']) {
        this.selectedFeeTier = params['fee'];
      }

      if (params['step'] === '2') {
        this.isAutoNavigating = true;
        const checkTokensInterval = setInterval(() => {
          if (this.poolTokens.length > 0 && this.selectedFeeTier && this.state.toToken() && this.state.toToken()?.address) {
            clearInterval(checkTokensInterval);
            this.goToStep2();
            this.isAutoNavigating = false;
          }
        }, 100);
        
        setTimeout(() => {
            clearInterval(checkTokensInterval);
            this.isAutoNavigating = false;
        }, 5000);
      }
    });

    this.startPriceUpdateInterval();
  }

  private priceUpdateInterval: any;

  startPriceUpdateInterval() {
    this.priceUpdateInterval = setInterval(async () => {
      if (this.activeStep === 2 && !this.isCalculatingRange && this.state.fromToken() && this.state.toToken()) {
        try {
          let poolPrice = await this.getInitialPoolPrice();
          if (this.baseToken === 'to') {
            poolPrice = 1 / poolPrice;
          }
          
          if (Math.abs(poolPrice - this.currentPrice) > 0.000001) {
            this.currentPrice = poolPrice;
            
            if (this.depositToken1) {
              this.onDepositToken1Change();
            } else if (this.depositToken2) {
              this.onDepositToken2Change();
            }
          }
        } catch (e) {
          console.warn('Failed to refresh pool price in background', e);
        }
      }
    }, 10000);
  }

  goBack() {
    this.router.navigate(['/liquidity']);
  }

  isCalculatingRange: boolean = false;
  realCurrentTick: number | null = null;
  realSqrtPriceX96: string | null = null;

  async swapTokens() {
    const temp = this.state.fromToken();
    if (this.state.toToken()) {
      this.isCalculatingRange = true;
      this.state.fromToken.set(this.state.toToken()!);
      this.state.toToken.set(temp);
      this.selectedFeeTier = '';
      this.currentPrice = await this.getInitialPoolPrice();
      if (this.baseToken === 'to') {
        this.currentPrice = 1 / this.currentPrice;
      }
      this.minPrice = this.currentPrice * 0.8;
      this.maxPrice = this.currentPrice * 1.2;
      this.isCalculatingRange = false;
      setTimeout(() => this.initChart(), 0);
    }
  }

  formatPlain(val: number | string): string {
    const num = Number(val);
    if (isNaN(num) || num === 0) return '0.00';

    const str = String(num);
    let plainStr = str;
    if (str.includes('e')) {
      const [base, exponentStr] = str.split('e');
      const exponent = parseInt(exponentStr, 10);
      let sign = base.startsWith('-') ? '-' : '';
      const cleanBase = base.replace('-', '').replace('.', '');
      
      if (exponent < 0) {
        const decPos = base.replace('-', '').indexOf('.');
        const zeros = '0'.repeat(Math.abs(exponent) - (decPos >= 0 ? 1 : 1));
        plainStr = `${sign}0.${zeros}${cleanBase}`;
      }
    }
    return plainStr;
  }

  formatSubscript(val: number | string): string {
    const num = Number(val);
    if (isNaN(num) || num === 0) return '0.00';
    if (num >= 1000) return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (num >= 0.01) return num.toPrecision(3);

    let plainStr = this.formatPlain(num).replace(/0+$/, '');
    const match = plainStr.match(/^0\.0+/);
    if (match) {
      const zeroCount = match[0].length - 2; 
      if (zeroCount >= 3) {
        const subscripts = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
        const countStr = zeroCount.toString().split('').map(d => subscripts[parseInt(d)]).join('');
        const sigDigits = plainStr.slice(match[0].length).slice(0, 4);
        return `0.0${countStr}${sigDigits}`;
      }
    }
    return plainStr.slice(0, 10);
  }

  private poolTokensSub?: Subscription;

  fetchPoolTokens() {
    const networkMap: Record<string, string> = {
      eth: 'ETH',
      bnb: 'BSC',
      matic: 'POLYGON',
      sepolia: 'ETH',
    };
    const activeNetwork = networkMap[this.state.selectedNetworkId()] || 'BSC';

    if (this.poolTokensSub) {
      this.poolTokensSub.unsubscribe();
    }

    this.poolTokensSub = this.http
      .get(`${environment.apiUrl}/user/liquidity/tokens?network=${activeNetwork}`)
      .subscribe({
        next: (res: any) => {
          if (res && res.status && res.data) {
            this.poolTokens = res.data
              .filter((t: any) => t.address?.toLowerCase() !== '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee')
              .map((t: any) => ({
              address: t.address,
              symbol: t.symbol,
              name: t.name,
              decimals: t.decimals || 18,
              rate: t.priceUsd || 0,
              change: 0,
              color: t.logoUrl ? 'transparent' : '#888888',
              logoUrl: t.logoUrl,
              pairs: t.pairs,
            }));

            if (this.poolTokens.length > 0) {
              const currentFrom = this.state.fromToken();

              if (!currentFrom || currentFrom.symbol === '---') {
                this.state.fromToken.set(this.poolTokens[0]);
              } else {
                const match = this.poolTokens.find(t => t.symbol.toUpperCase() === currentFrom.symbol.toUpperCase());
                if (match) {
                  this.state.fromToken.set(match);
                } else {
                  this.state.fromToken.set(this.poolTokens[0]);
                }
              }

              const currentTo = this.state.toToken();
              console.log('[DEBUG fetchPoolTokens] currentTo before check:', currentTo);
              
              if (currentTo && currentTo.symbol !== '---') {
                const match = this.poolTokens.find(t => t.symbol.toUpperCase() === currentTo.symbol.toUpperCase());
                if (match) {
                  console.log('[DEBUG fetchPoolTokens] Match found for toToken:', match);
                  this.state.toToken.set(match);
                } else {
                  console.log('[DEBUG fetchPoolTokens] NO MATCH found for', currentTo.symbol, '- falling back to', this.poolTokens.length > 1 ? this.poolTokens[1] : this.poolTokens[0]);
                  this.state.toToken.set(this.poolTokens.length > 1 ? this.poolTokens[1] : this.poolTokens[0]);
                }
              } else {
                console.log('[DEBUG fetchPoolTokens] currentTo empty, defaulting to:', this.poolTokens.length > 1 ? this.poolTokens[1] : this.poolTokens[0]);
                this.state.toToken.set(this.poolTokens.length > 1 ? this.poolTokens[1] : this.poolTokens[0]);
              }
            }
          }
        },
        error: (err) => console.error('[DEBUG fetchPoolTokens] Error fetching pool tokens:', err),
      });
  }

  get filteredLocalTokens() {
    const query = this.localTokenSearchQuery.toLowerCase().trim();
    let sourceList: any[] = [];
    const NATIVE_ADDR = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';

    if (this.localTokenModalTarget === 'from') {
      sourceList = this.poolTokens.filter(t => t.address?.toLowerCase() !== NATIVE_ADDR);
    } else {
      const fromTk = this.state.fromToken();
      if (fromTk && fromTk.address) {
        const poolData = this.poolTokens.find((t) => t.address === fromTk.address);
        if (poolData && poolData.pairs) {
          sourceList = this.poolTokens.filter((t) =>
            t.address?.toLowerCase() !== NATIVE_ADDR && poolData.pairs.some((p: any) => p.tokenAddress === t.address)
          );
        }
      }
    }

    if (!query) return sourceList;
    return sourceList.filter(
      (t) => t.symbol.toLowerCase().includes(query) || t.name.toLowerCase().includes(query),
    );
  }

  openLocalModal(target: 'from' | 'to') {
    this.localTokenModalTarget = target;
    this.localTokenSearchQuery = '';
    this.showLocalTokenModal = true;
  }

  closeLocalModal() {
    this.showLocalTokenModal = false;
  }

  selectLocalToken(token: any) {
    if (this.localTokenModalTarget === 'from') {
      this.state.toToken.set(null);
      this.state.fromToken.set(token);
      this.selectedFeeTier = '';
    } else {
      this.state.toToken.set(token);

      // Auto-select first available fee tier
      const tiers = this.availableFeeTiers;
      if (tiers && tiers.length > 0) {
        const tierMap: Record<number, string> = { 500: '0.05%', 3000: '0.30%', 10000: '1.00%' };
        this.selectedFeeTier = tierMap[tiers[0]] || '';
      } else {
        this.selectedFeeTier = '';
      }
    }

    this.closeLocalModal();
  }

  get availableFeeTiers(): number[] {
    const fromTk = this.state.fromToken();
    const toTk = this.state.toToken();
    if (!fromTk || !toTk || !fromTk.address || !toTk.address) return [];

    let poolData = this.poolTokens.find(
      (t) => t.address?.toLowerCase() === fromTk.address?.toLowerCase(),
    );
    let pairInfo = poolData?.pairs?.find(
      (p: any) => p.tokenAddress?.toLowerCase() === toTk.address?.toLowerCase(),
    );

    if (!pairInfo) {
      poolData = this.poolTokens.find(
        (t) => t.address?.toLowerCase() === toTk.address?.toLowerCase(),
      );
      pairInfo = poolData?.pairs?.find(
        (p: any) => p.tokenAddress?.toLowerCase() === fromTk.address?.toLowerCase(),
      );
    }

    if (!pairInfo) return [];
    return pairInfo.feeTiers || [];
  }

  selectTimeframe(tf: string) {
    this.activeTimeframe = tf;
    this.initChart();
  }

  private getGeckoNetworkName(chainId: number): string {
    switch (chainId) {
        case 1: return 'eth';
        case 56: return 'bsc';
        case 137: return 'polygon_pos';
        case 42161: return 'arbitrum';
        case 8453: return 'base';
        case 11155111: return 'eth';
        case 97: return 'bsc';
        case 80002: return 'polygon_pos';
        default: return 'eth';
    }
  }

  private async fetchPriceHistory(poolAddress: string) {
    if (!poolAddress) return;
    this.isChartLoading = true;
    try {
      const url = `${environment.apiUrl}/user/liquidity/pool/${poolAddress}/ohlcv?timeframe=${encodeURIComponent(this.activeTimeframe)}`;
      const { firstValueFrom } = await import('rxjs');
      const res: any = await firstValueFrom(this.http.get(url));
      
      if (res?.data?.attributes?.ohlcv_list) {
        this.historicalPriceData = res.data.attributes.ohlcv_list
          .map((pt: any) => {
             let closePrice = pt[4];
             if (this.baseToken === 'to') closePrice = 1 / closePrice;
             return [pt[0] * 1000, closePrice];
          })
          .reverse();
      }
    } catch (e) {
      console.warn('Failed to fetch historical prices', e);
    } finally {
      this.isChartLoading = false;
    }
  }

  applyStrategy(strategy: 'stable' | 'wide' | 'lower' | 'upper') {
    if (!this.currentPrice) return;
    
    this.rangeMode = 'custom';
    
    switch (strategy) {
      case 'stable':
        this.minPrice = this.currentPrice * 0.997;
        this.maxPrice = this.currentPrice * 1.003;
        break;
      case 'wide':
        this.minPrice = this.currentPrice * 0.50;
        this.maxPrice = this.currentPrice * 2.00;
        break;
      case 'lower':
        this.minPrice = this.currentPrice * 0.50;
        this.maxPrice = this.currentPrice * 0.999999;
        break;
      case 'upper':
        this.minPrice = this.currentPrice * 1.000001;
        this.maxPrice = this.currentPrice * 2.00;
        break;
    }
    
    this.updateChartMarkLines();
    if (this.depositToken1) this.onDepositToken1Change();
    else if (this.depositToken2) this.onDepositToken2Change();
  }

  toggleBaseToken(token: 'from' | 'to') {
    if (this.baseToken === token) return;
    this.baseToken = token;

    this.currentPrice = 1 / this.currentPrice;

    if (this.rangeMode === 'full') {
      this.minPrice = 0;
      this.maxPrice = 9999999;
    } else {
      const oldMin = this.minPrice;
      const oldMax = this.maxPrice;
      this.minPrice = oldMax === 0 ? 9999999 : 1 / oldMax;
      this.maxPrice = oldMin === 0 ? 9999999 : 1 / oldMin;

      if (this.minPrice > this.maxPrice) {
        const temp = this.minPrice;
        this.minPrice = this.maxPrice;
        this.maxPrice = temp;
      }
    }

    this.initChart();
    this.onDepositToken1Change();
  }

  preventScroll(event: Event) {
    event.preventDefault();
  }

  preventInvalidKeys(event: KeyboardEvent) {
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  sanitizeNumericString(value: any): string {
    if (value === null || value === undefined) return '';
    let valStr = String(value);
    valStr = valStr.replace(/[eE+-]/g, '');
    const parts = valStr.split('.');
    if (parts.length > 2) {
      valStr = parts[0] + '.' + parts.slice(1).join('');
    }
    if (valStr.includes('.')) {
      const p = valStr.split('.');
      if (p[1].length > 18) {
        valStr = p[0] + '.' + p[1].substring(0, 18);
      }
    }
    return valStr;
  }

  resetForm() {
    this.depositToken1 = '';
    this.depositToken2 = '';
    this.selectedFeeTier = '';
    this.activeStep = 1;

    this.minPrice = 0;
    this.maxPrice = 0;
    this.currentPrice = 0;
    this.rangeMode = 'custom';
    
    // Reset to default tokens
    if (this.poolTokens && this.poolTokens.length > 1) {
      this.state.fromToken.set(this.poolTokens[0]);
      this.state.toToken.set(this.poolTokens[1]);
    }

    // Clear URL parameters
    this.router.navigate(['/liquidity/new']);

    if (this.chartInstance) {
      try {
        this.updateChartMarkLines();
      } catch (e) {}
    }
  }

  // --- V3 Math Helpers --- //
  private get tickSpacing(): number {
    const feeStr = this.selectedFeeTier || '0.30%';
    if (feeStr === '0.05%') return 10;
    if (feeStr === '0.30%') return 60;
    if (feeStr === '1.00%') return 200;
    return 60;
  }

  private get feeAmountEnum(): number {
    const feeStr = this.selectedFeeTier;
    if (feeStr === '0.05%') return FeeAmount.LOW;
    if (feeStr === '0.30%') return FeeAmount.MEDIUM;
    if (feeStr === '1.00%') return FeeAmount.HIGH;
    return FeeAmount.MEDIUM; // Fallback, but validation will prevent reaching here if empty
  }

  private priceToTick(humanPrice: number, roundToUsable: boolean = true): number {
    const fromTk = this.state.fromToken();
    const toTk = this.state.toToken();
    if (!fromTk || !toTk) return 0;

    const isReversed = (fromTk?.address || '').toLowerCase() > (toTk?.address || '').toLowerCase();

    let actualHumanPrice = humanPrice;
    if (this.baseToken === 'to') {
      actualHumanPrice = 1 / humanPrice;
    }

    let priceToken1PerToken0 = actualHumanPrice;
    if (isReversed) priceToken1PerToken0 = 1 / actualHumanPrice;

    const dec0 = isReversed ? toTk.decimals || 18 : fromTk.decimals || 18;
    const dec1 = isReversed ? fromTk.decimals || 18 : toTk.decimals || 18;

    const ratioScaled = priceToken1PerToken0 * Math.pow(10, dec1 - dec0);

    let amount1_param, amount0_param;
    if (ratioScaled < 1e-18) {
      amount1_param = '1';
      amount0_param = Math.round(1 / ratioScaled).toLocaleString('fullwide', {useGrouping:false});
    } else {
      amount1_param = Math.round(ratioScaled * 1e18).toLocaleString('fullwide', {useGrouping:false});
      amount0_param = '1000000000000000000'; // 1e18
    }

    try {
      const sqrtPriceX96 = encodeSqrtRatioX96(amount1_param, amount0_param);
      const tick = TickMath.getTickAtSqrtRatio(sqrtPriceX96);
      return roundToUsable ? nearestUsableTick(tick, this.tickSpacing) : tick;
    } catch (e) {
      console.warn('priceToTick Error:', e, 'for humanPrice:', humanPrice);
      // If we are getting the tick for a Min/Max range calculation, clamping is acceptable.
      // But if we are calculating the initial price of the pool, clamping silently destroys the pool.
      // To be safe, we should alert the user that their price is invalid.
      throw new Error(`Invalid price input: ${humanPrice}. The mathematical ratio is too extreme or invalid.`);
    }
  }

  private async getInitialPoolPrice(): Promise<number> {
    const fromTk = this.state.fromToken();
    const toTk = this.state.toToken();
    if (!fromTk || !toTk) return 1;

    let poolPrice = 0;
    this.realCurrentTick = null;
    this.realSqrtPriceX96 = null;

    let poolData = this.poolTokens.find(
      (t) => t.address?.toLowerCase() === fromTk.address?.toLowerCase(),
    );
    let pairInfo = poolData?.pairs?.find(
      (p: any) => p.tokenAddress?.toLowerCase() === toTk.address?.toLowerCase(),
    );
    let isReverse = false;


    if (!pairInfo) {
      poolData = this.poolTokens.find(
        (t) => t.address?.toLowerCase() === toTk.address?.toLowerCase(),
      );
      pairInfo = poolData?.pairs?.find(
        (p: any) => p.tokenAddress?.toLowerCase() === fromTk.address?.toLowerCase(),
      );
      if (pairInfo) isReverse = true;
    }

    if (pairInfo) {
      let sqrt = parseFloat(pairInfo.sqrtPriceX96 || '0');

      if (pairInfo.poolAddress) {
        try {
          const slot0 = await this.web3Service.getExternalPoolSlot0(pairInfo.poolAddress);
          if (slot0) {
            sqrt = parseFloat(slot0.sqrtPriceX96.toString());
            if (sqrt > 0) {
              this.realSqrtPriceX96 = slot0.sqrtPriceX96.toString();
              this.realCurrentTick = Number(slot0.tick);
            }
          }
        } catch (e) {
          console.warn('Failed to fetch external pool slot0', e);
        }
      }

      if (sqrt > 0) {
        const ratio = sqrt / Math.pow(2, 96);
        let rawPrice = Math.pow(ratio, 2);
        const isBlockchainReverse = fromTk.address.toLowerCase() > toTk.address.toLowerCase();

        const dec0 = isBlockchainReverse ? (toTk.decimals || 18) : (fromTk.decimals || 18);
        const dec1 = isBlockchainReverse ? (fromTk.decimals || 18) : (toTk.decimals || 18);

        let priceOfToken0InToken1 = rawPrice * Math.pow(10, dec0 - dec1);

        let price = isBlockchainReverse ? (1 / priceOfToken0InToken1) : priceOfToken0InToken1;
        
        poolPrice = price;
      }
    }

    if (!poolPrice || isNaN(poolPrice)) {
      try {
        const contracts = this.web3Service.getContractsConfig();
        if (contracts && contracts.FACTORY) {
          const chainId = this.web3Service.chainId();
          
          let rpcUrl = (environment as any).rpcUrls?.[chainId];
          if (!rpcUrl && Object.values((environment as any).rpcUrls).length > 0) {
            rpcUrl = Object.values((environment as any).rpcUrls)[0];
          }

          const provider = new ethers.JsonRpcProvider(rpcUrl);
          const factoryAbi = ['function getPool(address,address,uint24) view returns (address)'];
          const factory = new ethers.Contract(contracts.FACTORY, factoryAbi, provider);
          const fee = parseInt((parseFloat(this.selectedFeeTier || '0.3') * 10000).toString());
          const poolAddr = await factory['getPool'](fromTk.address, toTk.address, fee);
          
          if (poolAddr && poolAddr !== '0x0000000000000000000000000000000000000000') {
            const poolAbi = ['function slot0() view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)'];
            const poolContract = new ethers.Contract(poolAddr, poolAbi, provider);
            const slot0 = await poolContract['slot0']();
            const sqrt = parseFloat(slot0.sqrtPriceX96.toString());
            if (sqrt > 0) {
              this.realSqrtPriceX96 = slot0.sqrtPriceX96.toString();
              this.realCurrentTick = Number(slot0.tick);

              const ratio = sqrt / Math.pow(2, 96);
              let rawPrice = Math.pow(ratio, 2);
              
              const isBlockchainReverse = fromTk.address.toLowerCase() > toTk.address.toLowerCase();
              const dec0 = isBlockchainReverse ? (toTk.decimals || 18) : (fromTk.decimals || 18);
              const dec1 = isBlockchainReverse ? (fromTk.decimals || 18) : (toTk.decimals || 18);
              
              let priceOfToken0InToken1 = rawPrice * Math.pow(10, dec0 - dec1);
              let price = isBlockchainReverse ? (1 / priceOfToken0InToken1) : priceOfToken0InToken1;
              poolPrice = price;
            }
          }
        }
      } catch (e) {
        console.warn('Failed to fetch on-chain pool price', e);
      }
    }

    if (!poolPrice || isNaN(poolPrice)) {
      poolPrice = fromTk.rate / toTk.rate;
    }

    if (!poolPrice || isNaN(poolPrice) || !isFinite(poolPrice)) {
      poolPrice = 1;
    }


    return poolPrice;
  }

  async goToStep2() {
    if (this.selectedFeeTier && this.state.toToken()) {
      this.activeStep = 2;
      this.isCalculatingRange = true;
      this.currentPrice = await this.getInitialPoolPrice();
      if (this.baseToken === 'to') {
        this.currentPrice = 1 / this.currentPrice;
      }
      
      let initialMin = this.currentPrice * 0.8;
      let initialMax = this.currentPrice * 1.2;
      let initialAmount1 = '';
      let initialAmount2 = '';

      const qParams = this.route.snapshot.queryParams;
      if (qParams['minPrice']) initialMin = Number(qParams['minPrice']);
      if (qParams['maxPrice']) initialMax = Number(qParams['maxPrice']);
      if (qParams['amount1']) initialAmount1 = qParams['amount1'];
      if (qParams['amount2']) initialAmount2 = qParams['amount2'];

      if (qParams['isFullRange'] === 'true') {
        this.rangeMode = 'full';
        this.minPrice = 0;
        this.maxPrice = 9999999;
      } else {
        this.rangeMode = 'custom';
        this.minPrice = initialMin;
        this.maxPrice = initialMax;
      }
      
      this.depositToken1 = initialAmount1;
      this.depositToken2 = initialAmount2;

      this.isCalculatingRange = false;
      setTimeout(() => this.initChart(), 0);

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          token1: this.state.fromToken().symbol,
          token2: this.state.toToken()?.symbol,
          fee: this.selectedFeeTier,
          step: 2
        },
        queryParamsHandling: 'merge'
      });
    }
  }

  editPair() {
    this.activeStep = 1;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: null },
      queryParamsHandling: 'merge'
    });
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  }

  selectFeeTier(tier: string) {
    this.selectedFeeTier = tier;
  }

  setRangeMode(mode: 'full' | 'custom') {
    this.rangeMode = mode;
    if (mode === 'full') {
      this.minPrice = 0;
      this.maxPrice = 9999999;
    } else {
      this.minPrice = this.currentPrice * 0.8;
      this.maxPrice = this.currentPrice * 1.2;
    }
    this.updateChartMarkLines();
    this.onDepositToken1Change();
  }

  onMinPriceChange() {
    this.minPrice = parseFloat(this.sanitizeNumericString(this.minPrice)) || 0;
    this.rangeMode = 'custom';
    this.updateChartMarkLines();
    this.onDepositToken1Change();
  }

  onMaxPriceChange() {
    this.maxPrice = parseFloat(this.sanitizeNumericString(this.maxPrice)) || 0;
    this.rangeMode = 'custom';
    this.updateChartMarkLines();
    this.onDepositToken1Change();
  }

  private tickToPriceExact(tick: number): number {
    const fromTk = this.state.fromToken();
    const toTk = this.state.toToken();
    if (!fromTk || !toTk) return 0;

    const isReversed = (fromTk?.address || '').toLowerCase() > (toTk?.address || '').toLowerCase();
    const dec0 = isReversed ? toTk.decimals || 18 : fromTk.decimals || 18;
    const dec1 = isReversed ? fromTk.decimals || 18 : toTk.decimals || 18;

    const sqrtRatioX96 = TickMath.getSqrtRatioAtTick(tick);
    const numerator = BigInt(sqrtRatioX96.toString()) * BigInt(sqrtRatioX96.toString());
    const denominator = BigInt(2) ** BigInt(192);
    let ratio = Number(numerator) / Number(denominator);
    let rawPrice = ratio * Math.pow(10, dec0 - dec1);

    if (isReversed) {
      rawPrice = 1 / rawPrice;
    }
    if (this.baseToken === 'to') {
      rawPrice = 1 / rawPrice;
    }
    return rawPrice;
  }

  private stepPrice(price: number, direction: 1 | -1): number {
    try {
      const currentTick = this.priceToTick(price, true);
      const p0 = this.tickToPriceExact(currentTick);
      const p1 = this.tickToPriceExact(currentTick + this.tickSpacing);
      const p2 = this.tickToPriceExact(currentTick - this.tickSpacing);
      
      const sorted = [p0, p1, p2].sort((a, b) => a - b);
      if (direction === 1) {
        return sorted.find(p => p > price + 1e-10) || (price * 1.01);
      } else {
        return sorted.slice().reverse().find(p => p < price - 1e-10) || (price * 0.99);
      }
    } catch {
      return direction === 1 ? price * 1.01 : price * 0.99;
    }
  }

  snapMinPrice() {
    if (this.rangeMode === 'full') return;
    try {
      const tick = this.priceToTick(this.minPrice, true);
      this.minPrice = this.tickToPriceExact(tick);
    } catch {}
    this.updateChartMarkLines();
    this.onDepositToken1Change();
  }

  snapMaxPrice() {
    if (this.rangeMode === 'full') return;
    try {
      const tick = this.priceToTick(this.maxPrice, true);
      this.maxPrice = this.tickToPriceExact(tick);
    } catch {}
    this.updateChartMarkLines();
    this.onDepositToken1Change();
  }

  incrementMin() {
    if (this.rangeMode === 'full') this.rangeMode = 'custom';
    this.minPrice = this.stepPrice(this.minPrice, 1);
    this.updateChartMarkLines();
    this.onDepositToken1Change();
  }
  decrementMin() {
    if (this.rangeMode === 'full') this.rangeMode = 'custom';
    this.minPrice = this.stepPrice(this.minPrice, -1);
    this.updateChartMarkLines();
    this.onDepositToken1Change();
  }
  incrementMax() {
    if (this.rangeMode === 'full') this.rangeMode = 'custom';
    this.maxPrice = this.stepPrice(this.maxPrice, 1);
    this.updateChartMarkLines();
    this.onDepositToken1Change();
  }
  decrementMax() {
    if (this.rangeMode === 'full') this.rangeMode = 'custom';
    this.maxPrice = this.stepPrice(this.maxPrice, -1);
    this.updateChartMarkLines();
    this.onDepositToken1Change();
  }

  get isToken1Disabled(): boolean {
    if (this.rangeMode === 'full') return false;
    return this.currentPrice >= this.maxPrice;
  }

  get isToken2Disabled(): boolean {
    if (this.rangeMode === 'full') return false;
    return this.currentPrice <= this.minPrice;
  }

  onDepositToken1Change() {
    this.depositToken1 = this.sanitizeNumericString(this.depositToken1);
    if (this.isToken2Disabled) {
      this.depositToken2 = '';
      return;
    }
    const amt1 = parseFloat(this.depositToken1) || 0;
    if (amt1 === 0) {
      this.depositToken2 = '';
      return;
    }

    if (this.rangeMode === 'full') {
      this.depositToken2 = (amt1 * this.currentPrice).toFixed(6);
      return;
    }

    try {
      const currentTick = this.realCurrentTick !== null ? this.realCurrentTick : this.priceToTick(this.currentPrice, false);
      const tickLower = this.priceToTick(this.minPrice);
      const tickUpper = this.priceToTick(this.maxPrice);

      const sqrtPriceX96 = this.realSqrtPriceX96 ? this.realSqrtPriceX96 : TickMath.getSqrtRatioAtTick(currentTick).toString();

      const fromTk = this.state.fromToken();
      const toTk = this.state.toToken();
      const isBlockchainReverse = (fromTk?.address || '').toLowerCase() > (toTk?.address || '').toLowerCase();
      
      const dec0 = isBlockchainReverse ? (toTk?.decimals || 18) : (fromTk?.decimals || 18);
      const dec1 = isBlockchainReverse ? (fromTk?.decimals || 18) : (toTk?.decimals || 18);

      const mockToken0 = new UniToken(1, '0x0000000000000000000000000000000000000001', dec0, 'TK0');
      const mockToken1 = new UniToken(1, '0x0000000000000000000000000000000000000002', dec1, 'TK1');
      const pool = new Pool(
        mockToken0,
        mockToken1,
        this.feeAmountEnum,
        sqrtPriceX96,
        1,
        currentTick,
      );

      let position;
      if (isBlockchainReverse) {
        position = Position.fromAmount1({
          pool: pool,
          tickLower: Math.min(tickLower, tickUpper),
          tickUpper: Math.max(tickLower, tickUpper),
          amount1: ethers.parseUnits(amt1.toFixed(dec1), dec1).toString(),
        });
        this.depositToken2 = ethers.formatUnits(position.amount0.quotient.toString(), dec0);
      } else {
        position = Position.fromAmount0({
          pool: pool,
          tickLower: Math.min(tickLower, tickUpper),
          tickUpper: Math.max(tickLower, tickUpper),
          amount0: ethers.parseUnits(amt1.toFixed(dec0), dec0).toString(),
          useFullPrecision: true,
        });
        this.depositToken2 = ethers.formatUnits(position.amount1.quotient.toString(), dec1);
      }
    } catch (e) {
      console.warn('V3 Math error:', e);
    }
  }

  onDepositToken2Change() {
    this.depositToken2 = this.sanitizeNumericString(this.depositToken2);
    if (this.isToken1Disabled) {
      this.depositToken1 = '';
      return;
    }
    const amt2 = parseFloat(this.depositToken2) || 0;
    if (amt2 === 0) {
      this.depositToken1 = '';
      return;
    }

    if (this.rangeMode === 'full') {
      this.depositToken1 = (amt2 / this.currentPrice).toFixed(6);
      return;
    }

    try {
      const currentTick = this.realCurrentTick !== null ? this.realCurrentTick : this.priceToTick(this.currentPrice, false);
      const tickLower = this.priceToTick(this.minPrice);
      const tickUpper = this.priceToTick(this.maxPrice);

      const sqrtPriceX96 = this.realSqrtPriceX96 ? this.realSqrtPriceX96 : TickMath.getSqrtRatioAtTick(currentTick).toString();

      const fromTk = this.state.fromToken();
      const toTk = this.state.toToken();
      const isBlockchainReverse = (fromTk?.address || '').toLowerCase() > (toTk?.address || '').toLowerCase();
      
      const dec0 = isBlockchainReverse ? (toTk?.decimals || 18) : (fromTk?.decimals || 18);
      const dec1 = isBlockchainReverse ? (fromTk?.decimals || 18) : (toTk?.decimals || 18);

      const mockToken0 = new UniToken(1, '0x0000000000000000000000000000000000000001', dec0, 'TK0');
      const mockToken1 = new UniToken(1, '0x0000000000000000000000000000000000000002', dec1, 'TK1');
      const pool = new Pool(
        mockToken0,
        mockToken1,
        this.feeAmountEnum,
        sqrtPriceX96,
        1,
        currentTick,
      );

      let position;
      if (isBlockchainReverse) {
        position = Position.fromAmount0({
          pool: pool,
          tickLower: Math.min(tickLower, tickUpper),
          tickUpper: Math.max(tickLower, tickUpper),
          amount0: ethers.parseUnits(amt2.toFixed(dec0), dec0).toString(),
          useFullPrecision: true,
        });
        this.depositToken1 = ethers.formatUnits(position.amount1.quotient.toString(), dec1);
      } else {
        position = Position.fromAmount1({
          pool: pool,
          tickLower: Math.min(tickLower, tickUpper),
          tickUpper: Math.max(tickLower, tickUpper),
          amount1: ethers.parseUnits(amt2.toFixed(dec1), dec1).toString(),
        });
        this.depositToken1 = ethers.formatUnits(position.amount0.quotient.toString(), dec0);
      }
    } catch (e) {
      console.warn('V3 Math error:', e);
    }
  }

  ngAfterViewInit() {
    if (this.activeStep === 2) {
      setTimeout(() => this.initChart(), 0);
    }
  }

  ngOnDestroy() {
    if (this.priceUpdateInterval) clearInterval(this.priceUpdateInterval);
    if (this.poolTokensSub) this.poolTokensSub.unsubscribe();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.chartInstance) this.chartInstance.dispose();
  }

  getLiquidityValidationState(): { isValid: boolean; message: string } {
    const amt1 = parseFloat(this.depositToken1) || 0;
    const amt2 = parseFloat(this.depositToken2) || 0;

    if (!this.state.toToken()) {
      return { isValid: false, message: 'Select second token' };
    }
    if (amt1 <= 0 && amt2 <= 0) {
      return { isValid: false, message: 'Enter Amounts' };
    }
    if (this.rangeMode === 'custom' && this.minPrice >= this.maxPrice) {
      return { isValid: false, message: 'Invalid Price Range' };
    }
    if (!this.selectedFeeTier) {
      return { isValid: false, message: 'Select Fee Tier' };
    }
    if (!this.depositToken1 || !this.depositToken2 || parseFloat(this.depositToken1) <= 0 || parseFloat(this.depositToken2) <= 0) {
      return { isValid: false, message: 'Enter an amount' };
    }
    if (this.state.isWalletConnected()) {
      const bal1 = parseFloat(this.state.tokenBalances()[this.state.fromToken().address?.toLowerCase() || ''] || '0');
      const bal2 = parseFloat(this.state.tokenBalances()[this.state.toToken()?.address?.toLowerCase() || ''] || '0');

      const isInsufficient1 = parseFloat(this.depositToken1) > bal1;
      const isInsufficient2 = parseFloat(this.depositToken2) > bal2;

      if (isInsufficient1 && isInsufficient2) {
        return { isValid: false, message: `Insufficient ${this.state.fromToken().symbol} and ${this.state.toToken()?.symbol} balance` };
      } else if (isInsufficient1) {
        return { isValid: false, message: `Insufficient ${this.state.fromToken().symbol} balance` };
      } else if (isInsufficient2) {
        return { isValid: false, message: `Insufficient ${this.state.toToken()?.symbol} balance` };
      }
    }
    return { isValid: true, message: 'Add Liquidity' };
  }

  getActivePositionManagerAddress(pairInfo: any): string {
    if (pairInfo?.liquidityType === 'external') {
      const contracts = this.web3Service.getContractsConfig();
      return contracts.DEXOR_EXTERNAL_LIQUIDITY_ROUTER;
    }
    const contracts = this.web3Service.getContractsConfig();
    return contracts.POSITION_MANAGER;
  }

  isSubmitting = false;

  async addLiquidity() {
    if (!this.web3Service.isConnected()) {
      this.state.openConnectModal();
      return;
    }

    try {
      this.isSubmitting = true;
      this.state.triggerNotification('Initiating add liquidity...');

      if (!this.state.fromToken().address || !this.state.toToken()?.address) {
        throw new Error('Selected tokens do not have valid contract addresses.');
      }

      const t0Addr = this.state.fromToken().address!;
      const t1Addr = this.state.toToken()!.address!;

      const finalDep1 = this.isToken1Disabled ? '0' : (this.depositToken1 || '0');
      const finalDep2 = this.isToken2Disabled ? '0' : (this.depositToken2 || '0');
      
      const amt0 = ethers.parseUnits(
        finalDep1.toString(),
        this.state.fromToken().decimals || 18,
      );
      const amt1 = ethers.parseUnits(
        finalDep2.toString(),
        this.state.toToken()!.decimals || 18,
      );

      let poolData = this.poolTokens.find(
        (t) => t.address?.toLowerCase() === t0Addr?.toLowerCase(),
      );
      let pairInfo = poolData?.pairs?.find(
        (p: any) => p.tokenAddress?.toLowerCase() === t1Addr?.toLowerCase(),
      );
      let isReverse = false;


      if (!pairInfo) {
        poolData = this.poolTokens.find((t) => t.address?.toLowerCase() === t1Addr?.toLowerCase());
        pairInfo = poolData?.pairs?.find(
          (p: any) => p.tokenAddress?.toLowerCase() === t0Addr?.toLowerCase(),
        );
        if (pairInfo) isReverse = true;
      }

      const activeManagerAddress = this.getActivePositionManagerAddress(pairInfo);
      const isExternal = pairInfo?.liquidityType === 'external';

      await this.web3Service.switchNetwork(this.state.selectedNetworkId());

      let tx;

      const t0Contract = new ethers.Contract(t0Addr, ERC20_ABI, await this.web3Service.getEthersProvider());
      const t1Contract = new ethers.Contract(t1Addr, ERC20_ABI, await this.web3Service.getEthersProvider());
      
      if (amt0 > 0n) {
        await this.web3Service.checkAndApprove(t0Addr, activeManagerAddress, amt0);
      }
      if (amt1 > 0n) {
        await this.web3Service.checkAndApprove(t1Addr, activeManagerAddress, amt1);
      }

      const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

      let feeNum = this.feeAmountEnum;

      const isReversed = t0Addr.toLowerCase() > t1Addr.toLowerCase();
      const sortedToken0 = isReversed ? t1Addr : t0Addr;
      const sortedToken1 = isReversed ? t0Addr : t1Addr;
      const sortedAmt0 = isReversed ? amt1 : amt0;
      const sortedAmt1 = isReversed ? amt0 : amt1;

      console.log('====== DEBUG ADD LIQUIDITY ======');
      console.log('1. User Inputs:', { minPrice: this.minPrice, maxPrice: this.maxPrice, currentPrice: this.currentPrice, depositToken1: this.depositToken1, depositToken2: this.depositToken2 });
      console.log('2. Tokens:', { fromTk: t0Addr, toTk: t1Addr, isReversed, sortedToken0, sortedToken1 });
      console.log('3. Disable Logic:', { isToken1Disabled: this.isToken1Disabled, isToken2Disabled: this.isToken2Disabled });
      console.log('4. Initial Amounts (Parsed):', { amt0: amt0.toString(), amt1: amt1.toString() });
      console.log('5. Sorted Amounts:', { sortedAmt0: sortedAmt0.toString(), sortedAmt1: sortedAmt1.toString() });

      let tLower = nearestUsableTick(TickMath.MIN_TICK, this.tickSpacing);
      let tUpper = nearestUsableTick(TickMath.MAX_TICK, this.tickSpacing);

      if (this.rangeMode === 'custom') {
        let tick1 = nearestUsableTick(this.priceToTick(this.minPrice), this.tickSpacing);
        let tick2 = nearestUsableTick(this.priceToTick(this.maxPrice), this.tickSpacing);
        if (tick1 === tick2) {
          tick2 += this.tickSpacing;
        }
        tLower = Math.min(tick1, tick2);
        tUpper = Math.max(tick1, tick2);
      }

      const initTick = this.priceToTick(this.currentPrice, false);
      const initSqrtPriceX96 = TickMath.getSqrtRatioAtTick(initTick).toString();
      
      console.log('6. Tick Math:', { initTick, initSqrtPriceX96, tickLower: tLower, tickUpper: tUpper, tickSpacing: this.tickSpacing });

      const params = {
        token0: sortedToken0,
        token1: sortedToken1,
        fee: feeNum,
        tickLower: tLower,
        tickUpper: tUpper,
        amount0Desired: sortedAmt0,
        amount1Desired: sortedAmt1,
        amount0Min: 0n,
        amount1Min: 0n,
        recipient: this.web3Service.address(),
        deadline: deadline,
      };
      
      console.log('7. Final MintParams:', params);
      console.log('=================================');

      const contracts = this.web3Service.getContractsConfig();
      let finalManagerAddress = activeManagerAddress;
      if (isExternal) {
        if (pairInfo.dexName === 'Uniswap') {
          finalManagerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
        } else if (pairInfo.dexName === 'PancakeSwap') {
          finalManagerAddress = contracts.EXTERNAL_MANAGERS?.PancakeSwap || '0x46A15B0b27311cedF172AB29E4f4766fbE7F4364';
        } else {
          finalManagerAddress = contracts.EXTERNAL_MANAGERS?.Uniswap || '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';
        }
        
        const router = await this.web3Service.getContract(
          activeManagerAddress,
          EXTERNAL_ROUTER_ABI,
        );
        const addLiqParams = {
          mintParams: params,
          initializePool: true,
          initSqrtPriceX96: initSqrtPriceX96,
        };

        tx = await router['addLiquidity'](finalManagerAddress, addLiqParams);
      } else {
        const manager = await this.web3Service.getContract(
          activeManagerAddress,
          POSITION_MANAGER_ABI,
        );
        const initData = manager.interface.encodeFunctionData('createAndInitializePoolIfNecessary', [
          sortedToken0,
          sortedToken1,
          feeNum,
          initSqrtPriceX96,
        ]);
        const mintData = manager.interface.encodeFunctionData('mint', [params]);
        tx = await manager['multicall']([initData, mintData]);
      }
      
      this.state.triggerNotification('Transaction submitted! Waiting for confirmation...');
      const receipt = await tx.wait();

      const authHeaders = { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` };
      let frontendPoolAddress = pairInfo?.poolAddress || '';
      let parsedTokenId = 0;
      let parsedLiquidity = 0;
      let parsedLiquidityStr = '0';
      let actualToken0Amount = 0;
      let actualToken1Amount = 0;

      try {
        const INCREASE_LIQ_TOPIC = '0x3067048beee31b25b2f1681f88dac838c8bba36af25bfb2b7cf7473a5847e35f';
        const POOL_CREATED_TOPIC = '0x783cca1c0412dd0d695e1e56b8cc62744e213324f6057262dc179b00799f9e31';
        
        for (const log of receipt.logs) {
          if (log.topics[0] === INCREASE_LIQ_TOPIC) {
            parsedTokenId = Number(BigInt(log.topics[1]));
            try {
              const decoded = ethers.AbiCoder.defaultAbiCoder().decode(['uint128', 'uint256', 'uint256'], log.data);
              parsedLiquidity = Number(ethers.formatUnits(decoded[0], 18));
              parsedLiquidityStr = decoded[0].toString();
              
              const dec0 = isReversed ? (this.state.toToken()?.decimals || 18) : (this.state.fromToken()?.decimals || 18);
              const dec1 = isReversed ? (this.state.fromToken()?.decimals || 18) : (this.state.toToken()?.decimals || 18);
              actualToken0Amount = Number(ethers.formatUnits(decoded[1], dec0));
              actualToken1Amount = Number(ethers.formatUnits(decoded[2], dec1));
            } catch(e) { console.error("Failed to decode liquidity", e); }
          }
          if (!frontendPoolAddress && log.topics[0] === POOL_CREATED_TOPIC) {
            frontendPoolAddress = '0x' + log.data.slice(26, 66);
          }
        }
      } catch(e) {
        console.error("Failed to parse logs on frontend:", e);
      }
      
      try {
        await import('rxjs').then((m) =>
          m.firstValueFrom(
            this.http.post(
              `${environment.apiUrl}/user/liquidity/user-positions/sync`,
              { 
                txHash: tx.hash,
                tokenId: parsedTokenId,
                action: 'add',
                liquidity: parsedLiquidity,
                liquidityStr: parsedLiquidityStr,
                isExternal: isExternal,
                managerAddress: finalManagerAddress,
                networkId: this.state.selectedNetworkId(),
                tickLower: tLower,
                tickUpper: tUpper,
                fee: feeNum,
                token0: sortedToken0,
                token1: sortedToken1,
                poolAddress: frontendPoolAddress,
                token0Amount: actualToken0Amount > 0 ? actualToken0Amount : (isReversed ? Number(this.depositToken2) : Number(this.depositToken1)),
                token1Amount: actualToken1Amount > 0 ? actualToken1Amount : (isReversed ? Number(this.depositToken1) : Number(this.depositToken2))
              },
              { headers: authHeaders },
            ),
          ),
        );
      } catch (syncErr) {
        console.error('Failed to sync position with backend:', syncErr);
      }

      try {
        const usdValue0 = (Number(this.depositToken1) || 0) * (this.state.fromToken()?.rate || 0);
        const usdValue1 = (Number(this.depositToken2) || 0) * (this.state.toToken()?.rate || 0);
        const totalUsdValue = usdValue0 + usdValue1;
        let feeEarned = 0;
        let feeEarned1 = 0;
        
        if (isExternal) {
            try {
                const routerContract = await this.web3Service.getContract(activeManagerAddress, ['function feePercentage() view returns (uint256)']);
                const feePercentageBps = await routerContract['feePercentage']();
                feeEarned = (Number(this.depositToken1) * Number(feePercentageBps)) / 10000;
                feeEarned1 = (Number(this.depositToken2) * Number(feePercentageBps)) / 10000;
            } catch (feeErr) {
                console.error('Failed to read fee percentage from router:', feeErr);
            }
        }

        const logPayload = {
            walletAddress: this.web3Service.address(),
            txHash: tx.hash,
            token0Address: t0Addr,
            token0Symbol: this.state.fromToken().symbol,
            token1Address: t1Addr,
            token1Symbol: this.state.toToken()?.symbol || '',
            amount0: Number(this.depositToken1) || 0,
            amount1: Number(this.depositToken2) || 0,
            usdValue: totalUsdValue,
            feeEarned: feeEarned,
            feeEarned1: feeEarned1,
            action: 'Add',
            liquidityType: isExternal ? 'EXTERNAL' : 'INTERNAL',
            platformName: pairInfo?.dexName || 'Internal',
            chainId: this.state.selectedNetworkId() || 1
        };

        const authHeadersForLog = { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` };
        
        await import('rxjs').then((m) =>
          m.firstValueFrom(
            this.http.post(`${environment.apiUrl}/user/liquidity/log`, logPayload, { headers: authHeadersForLog })
          )
        ).then(() => console.log('Liquidity log recorded'))
         .catch((err) => console.error('Failed to record liquidity log:', err));

        await import('rxjs').then((m) =>
          m.firstValueFrom(
            this.activityService.recordActivity({
              txHash: tx.hash,
              chainId: this.web3Service.chainId() || 1,
              actionType: 'ADD_LIQUIDITY',
              status: 'Success',
              payload: {
                amount0: Number(this.depositToken1) || 0,
                amount1: Number(this.depositToken2) || 0,
                token0Symbol: this.state.fromToken().symbol,
                token1Symbol: this.state.toToken()?.symbol || '',
                usdValue: totalUsdValue,
                poolAddress: pairInfo?.poolAddress || ''
              },
              platformName: isExternal ? 'External Aggregator' : 'DEX Native'
            })
          )
        ).then(() => console.log('Unified activity recorded'))
         .catch((err) => console.error('Failed to record unified activity:', err));

      } catch (logErr) {
        console.error('Failed to post liquidity log:', logErr);
      }

      this.state.triggerNotification('Liquidity added successfully!');
      this.state.fetchBalances();
      this.router.navigate(['/liquidity']);
    } catch (e: any) {
      console.error(e);
      this.state.triggerNotification(this.state.handleError(e, 'Add Liquidity failed'));
    } finally {
      this.isSubmitting = false;
    }
  }

  private updateChartMarkLines() {
    if (!this.chartInstance) return;
    const option = this.chartInstance.getOption() as any;
    const actualMin = this.rangeMode === 'full' ? 0 : this.minPrice;
    const actualMax = this.rangeMode === 'full' ? 9999999 : this.maxPrice;

    if (option && option.series && option.series[0]) {
      if (option.visualMap && option.visualMap.length > 0) {
        option.visualMap[0].pieces = [
          { lte: actualMin, color: 'rgba(100, 116, 139, 0.15)' },
          { gt: actualMin, lte: this.currentPrice, color: '#3b82f6' },
          { gt: this.currentPrice, lt: actualMax, color: '#10b981' },
          { gte: actualMax, color: 'rgba(100, 116, 139, 0.15)' },
        ];
      }

      this.chartInstance.setOption(option);
    }
    
    this.updateHandlePositions();
    
    // Clear disabled token amounts dynamically
    if (this.isToken1Disabled) {
      this.depositToken1 = '';
    }
    if (this.isToken2Disabled) {
      this.depositToken2 = '';
    }
  }

  private async initChart() {
    if (!this.chartContainer?.nativeElement) return;
    if (!this.chartInstance) {
      this.chartInstance = echarts.init(this.chartContainer.nativeElement);
    }

    // Adjust spread based on timeframe
    let spread = 0.2;
    if (this.activeTimeframe === '1D') spread = 0.05;
    if (this.activeTimeframe === '1W') spread = 0.1;
    if (this.activeTimeframe === '1M') spread = 0.2;
    if (this.activeTimeframe === '1Y') spread = 0.5;
    if (this.activeTimeframe === 'All time') spread = 0.8;

    const chartSpread = spread * 1.1; // 10% padding so maxPrice/minPrice are visible
    let minX = this.currentPrice * (1 - chartSpread);
    let maxX = this.currentPrice * (1 + chartSpread);
    
    if (this.rangeMode === 'custom') {
      if (this.minPrice < minX) minX = this.minPrice * 0.95;
      if (this.maxPrice > maxX) maxX = this.maxPrice * 1.05;
    }

    let data: [number, number][] = [];

    try {
      const fromTk = this.state.fromToken();
      const toTk = this.state.toToken();
      const poolData = this.poolTokens.find((t) => t.address === fromTk.address);

      let poolAddress = '';
      let isExternal = false;
      let dexName = '';
      
      if (poolData && poolData.pairs && toTk) {
        const pairInfo = poolData.pairs.find((p: any) => p.tokenAddress === toTk.address);
        if (pairInfo && pairInfo.poolAddress) {
          poolAddress = pairInfo.poolAddress;
          isExternal = pairInfo.liquidityType === 'external';
          dexName = pairInfo.dexName;
        }
      }

      if (poolAddress) {
        import('rxjs').then(async ({ firstValueFrom }) => {
          let ticks: any[] = [];
          
          this.isChartLoading = true;
          try {
            const res: any = await firstValueFrom(
              this.http.get(`${environment.apiUrl}/user/liquidity/pool/${poolAddress}/ticks`),
            );
            
            if (res && res.status && res.data && res.data.ticks) {
              let L = 0;
              ticks = res.data.ticks.sort((a: any, b: any) => a.tickIdx - b.tickIdx).map((t: any) => {
                L += Number(ethers.formatUnits(t.liquidityNet, 0));
                return { tickIdx: t.tickIdx, absoluteLiquidity: L };
              });
            }

            await this.fetchPriceHistory(poolAddress);
          } catch (e) {
            console.warn(`[CRITICAL] Backend API fetch failed`, e);
          } finally {
            this.isChartLoading = false;
          }

          let maxLiquidity = 0;
          if (ticks.length > 0) {
            const pricePoints: { price: number; liquidity: number }[] = [];
            const isBlockchainReverse = fromTk.address.toLowerCase() > toTk.address.toLowerCase();
            const dec0 = isBlockchainReverse ? (toTk.decimals || 18) : (fromTk.decimals || 18);
            const dec1 = isBlockchainReverse ? (fromTk.decimals || 18) : (toTk.decimals || 18);

            for (let i = 0; i < ticks.length; i++) {
              const sqrtPriceX96 = TickMath.getSqrtRatioAtTick(ticks[i].tickIdx);
              const ratio = Number(sqrtPriceX96) / Math.pow(2, 96);
              let rawPrice = Math.pow(ratio, 2);
              
              let priceOfToken0InToken1 = rawPrice * Math.pow(10, dec0 - dec1);
              let price = isBlockchainReverse ? (1 / priceOfToken0InToken1) : priceOfToken0InToken1;
              
              if (this.baseToken === 'to') price = 1 / price;

              pricePoints.push({ price, liquidity: ticks[i].absoluteLiquidity });
            }

            pricePoints.sort((a, b) => a.price - b.price);

            const NUM_BINS = 60;
            const binSize = (maxX - minX) / NUM_BINS;

            const getLiquidityAtPrice = (targetPrice: number) => {
              let liq = 0;
              for (let i = 0; i < pricePoints.length; i++) {
                if (pricePoints[i].price <= targetPrice) {
                  liq = pricePoints[i].liquidity;
                } else {
                  break;
                }
              }
              return liq;
            };

            for (let j = 0; j < NUM_BINS; j++) {
              const binCenter = minX + (j + 0.5) * binSize;
              const liq = getLiquidityAtPrice(binCenter);
              if (liq > maxLiquidity) maxLiquidity = liq;
              data.push([binCenter, liq]);
            }
          }

          if (data.length === 0) {
            const NUM_BINS = 60;
            const binSize = (maxX - minX) / NUM_BINS;
            for (let j = 0; j < NUM_BINS; j++) {
              data.push([minX + (j + 0.5) * binSize, 0]);
            }
          }

          if (data.every((d: any) => d[1] === 0)) {
             console.warn('[WARNING] Real Subgraph API returned 0 liquidity for this pool!');
          }

          console.log('CHART DATA:', JSON.stringify(data));

          this.renderECharts(data, minX, maxX, maxLiquidity);
        });
        return; // async update
      }
    } catch (e) {
      console.warn('Could not fetch ticks, using empty data', e);
    }

    if (data.length === 0) {
      const NUM_BINS = 60;
      const binSize = (maxX - minX) / NUM_BINS;
      for (let j = 0; j < NUM_BINS; j++) {
        data.push([minX + (j + 0.5) * binSize, 0]);
      }
    }

    this.renderECharts(data, minX, maxX, 0);
  }

  zoomChart(direction: 'in' | 'out') {
    if (!this.chartInstance) return;
    
    // ECharts provides dataZoom components. We need to dispatch a dataZoom action.
    const currentOption = this.chartInstance.getOption() as any;
    if (!currentOption || !currentOption.dataZoom || currentOption.dataZoom.length === 0) {
      // If there's no dataZoom component active, we adjust the xAxis min/max manually
      if (currentOption.xAxis && currentOption.xAxis[0]) {
        const min = currentOption.xAxis[0].min as number;
        const max = currentOption.xAxis[0].max as number;
        const range = max - min;
        
        let newMin = min;
        let newMax = max;
        
        if (direction === 'in') {
          newMin = min + range * 0.1;
          newMax = max - range * 0.1;
        } else {
          newMin = min - range * 0.1;
          newMax = max + range * 0.1;
        }
        
        // Prevent zooming out past 0
        newMin = Math.max(0, newMin);
        
        this.chartInstance.setOption({
          xAxis: {
            min: newMin,
            max: newMax
          }
        });
      }
      return;
    }

    const dataZoom = currentOption.dataZoom[0];
    const start = dataZoom.start;
    const end = dataZoom.end;
    const range = end - start;

    let newStart = start;
    let newEnd = end;

    if (direction === 'in') {
      const zoomFactor = range * 0.2; // increase zoom step for better UX
      newStart = Math.max(0, start + zoomFactor);
      newEnd = Math.min(100, end - zoomFactor);
      // prevent crossing or zooming in too far
      if (newEnd - newStart < 2) {
        const mid = (start + end) / 2;
        newStart = Math.max(0, mid - 1);
        newEnd = Math.min(100, mid + 1);
      }
    } else {
      const zoomFactor = range * 0.2;
      newStart = Math.max(0, start - zoomFactor);
      newEnd = Math.min(100, end + zoomFactor);
    }

    this.chartInstance.dispatchAction({
      type: 'dataZoom',
      start: newStart,
      end: newEnd,
      dataZoomIndex: [0, 1] // Ensure both inside and slider zooms are updated
    });
  }

  resetZoom() {
    if (!this.chartInstance) return;
    
    // Fetch fresh limits or simply reset to full range
    const currentOption = this.chartInstance.getOption() as any;
    if (currentOption && currentOption.dataZoom && currentOption.dataZoom.length > 0) {
      // Force reset via setOption for guaranteed application
      this.chartInstance.setOption({
        dataZoom: [
          { start: 0, end: 100 },
          { start: 0, end: 100 }
        ]
      });
      // Also restore the default view
      this.chartInstance.dispatchAction({ type: 'restore' });
    } else if (this.currentPrice) {
      // Reset manual zoom if no dataZoom component
      this.chartInstance.setOption({
        xAxis: {
          min: this.currentPrice * 0.5,
          max: this.currentPrice * 1.5
        }
      });
    }
    
    // Update graphic overlay handles after resetting zoom
    setTimeout(() => this.updateHandlePositions(), 50);
  }


  private renderECharts(data: any[], minX: number, maxX: number, maxLiquidity: number) {
    if (!this.chartInstance) return;

    const option: echarts.EChartsOption = {
      grid: { top: 50, bottom: 50, left: 20, right: 60 },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: [0, 1],
          filterMode: 'none',
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
          moveOnMouseWheel: true
        },
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0, 1],
          filterMode: 'none',
          bottom: 5,
          height: 30,
          borderColor: 'rgba(255,255,255,0.05)',
          backgroundColor: 'rgba(15, 23, 42, 0.8)',
          fillerColor: 'rgba(59, 130, 246, 0.3)',
          handleSize: '160%',
          handleStyle: { color: '#3b82f6', borderWidth: 0, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.5)' },
          showDataShadow: false,
          textStyle: { color: '#94a3b8' }
        }
      ],
      xAxis: [
        {
          type: 'value',
          name: '',
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: { color: '#94a3b8', fontSize: 12 },
          axisLine: { show: true, lineStyle: { color: '#334155' } },
          axisTick: { show: false },
          axisLabel: {
            color: '#94a3b8',
            fontSize: 11,
            margin: 12,
            formatter: (val: number) => this.formatSubscript(val),
          },
          splitLine: { show: false },
        },
        {
          type: 'time',
          show: true,
          position: 'top',
          name: 'Time',
          nameLocation: 'middle',
          nameGap: 25,
          nameTextStyle: { color: '#64748b', fontSize: 12 },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { 
            color: '#64748b',
            fontSize: 10,
            margin: 8
          },
          splitLine: { show: false }
        }
      ],
      yAxis: [
        {
          type: 'value',
          show: false,
          min: 0,
          max: maxLiquidity === 0 ? 1 : maxLiquidity * 1.2,
        },
        {
          type: 'value',
          show: true,
          position: 'right',
          name: '',
          nameLocation: 'middle',
          nameGap: 45,
          nameTextStyle: { color: '#F59E0B', fontSize: 12 },
          min: 'dataMin',
          max: 'dataMax',
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { 
            color: '#F59E0B',
            fontSize: 11,
            formatter: (val: number) => this.formatSubscript(val),
          },
          splitLine: { show: true, lineStyle: { color: '#1e293b', type: 'dashed' } }
        }
      ],
      series: [
        {
          data: data,
          type: 'line',
          xAxisIndex: 0,
          yAxisIndex: 0,
          step: 'middle', // Creates the histogram/bucket effect
          smooth: false,
          showSymbol: false,
          areaStyle: {
            opacity: 0.85,
          },
          lineStyle: {
            width: 1, // Thin border around the blocks
          },
          markLine: {
            symbol: ['none', 'none'],
            label: { show: false },
            animation: false,
            silent: true,
            data: [
              {
                xAxis: this.currentPrice,
                lineStyle: { color: '#fff', width: 2, type: 'dashed', opacity: 0.5 },
                label: { show: true, formatter: 'Current', position: 'insideStartTop', color: '#fff', backgroundColor: 'transparent' }
              },
            ],
          },
        },
        {
          data: this.historicalPriceData,
          type: 'line',
          xAxisIndex: 1,
          yAxisIndex: 1,
          smooth: true,
          showSymbol: false,
          lineStyle: {
            color: '#F59E0B', // Gold/amber color for the price history line
            width: 1.5,
          }
        }
      ],
      visualMap: [
        {
          type: 'piecewise',
          show: false,
          dimension: 0,
          seriesIndex: 0,
          pieces: [
            { lte: this.minPrice, color: 'rgba(100, 116, 139, 0.15)' }, // Deeper faded gray
            { gt: this.minPrice, lte: this.currentPrice, color: '#3b82f6' }, // Premium Solid Blue
            { gt: this.currentPrice, lt: this.maxPrice, color: '#10b981' }, // Premium Solid Green
            { gte: this.maxPrice, color: 'rgba(100, 116, 139, 0.15)' },
          ],
        },
      ],
    };

    this.chartInstance.setOption(option, true);
    
    // Bind handle updates to zoom and pan events
    this.chartInstance.off('dataZoom');
    this.chartInstance.on('dataZoom', () => {
      this.updateHandlePositions();
    });

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.chartInstance?.resize();
        this.updateHandlePositions();
      });
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  } // <-- End of renderECharts

  setMaxToken1() {
    const fromTk = this.state.fromToken();
    if (!fromTk || !fromTk.address) return;
    const balance = this.state.tokenBalances()[fromTk.address.toLowerCase()];
    if (!balance || parseFloat(balance) <= 0) return;
    this.depositToken1 = balance;
    this.onDepositToken1Change();
  }

  setMaxToken2() {
    const toTk = this.state.toToken();
    if (!toTk || !toTk.address) return;
    const balance = this.state.tokenBalances()[toTk.address.toLowerCase()];
    if (!balance || parseFloat(balance) <= 0) return;
    this.depositToken2 = balance;
    this.onDepositToken2Change();
  }
}